import {
  goalListResponseSchema,
  type GoalListResponse,
  type GoalFilters,
  type Goal,
} from '../types';
import {
  authFetch, API_BASE_URL 
} from '@/lib/api-client';

const mockGoals: GoalListResponse = {
  data: [
    {
      goalId: 'goal-001',
      employeeId: 'emp-101',
      employeeName: 'Maria Garcia',
      employeeRole: 'Line Cook',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      title: 'Complete Advanced Culinary Certification',
      description: 'Obtain the Advanced Culinary Arts certification to enhance plating and presentation skills.',
      category: 'certification',
      priority: 'high',
      status: 'in-progress',
      progress: 65,
      targetDate: '2026-03-31',
      assignedBy: 'user-001',
      assignedByName: 'Chef Martinez',
      milestones: [
        {
          id: 'm1',
          title: 'Complete online modules',
          completed: true,
          completedAt: '2026-01-15T10:00:00Z' 
        },
        {
          id: 'm2',
          title: 'Pass written exam',
          completed: true,
          completedAt: '2026-01-25T14:00:00Z' 
        },
        {
          id: 'm3',
          title: 'Complete practical assessment',
          completed: false,
          completedAt: null 
        },
      ],
      createdAt: '2026-01-01T09:00:00Z',
      completedAt: null,
    },
    {
      goalId: 'goal-002',
      employeeId: 'emp-102',
      employeeName: 'James Wilson',
      employeeRole: 'Storekeeper',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      title: 'Reduce Inventory Variance to Under 2%',
      description: 'Implement improved counting procedures to reduce inventory variance from 4% to under 2%.',
      category: 'efficiency',
      priority: 'critical',
      status: 'in-progress',
      progress: 80,
      targetDate: '2026-02-28',
      assignedBy: 'user-002',
      assignedByName: 'Sarah Chen',
      milestones: [
        {
          id: 'm1',
          title: 'Audit current procedures',
          completed: true,
          completedAt: '2026-01-10T09:00:00Z' 
        },
        {
          id: 'm2',
          title: 'Implement new counting schedule',
          completed: true,
          completedAt: '2026-01-20T11:00:00Z' 
        },
        {
          id: 'm3',
          title: 'Train team on new procedures',
          completed: true,
          completedAt: '2026-01-25T15:00:00Z' 
        },
        {
          id: 'm4',
          title: 'Achieve target variance',
          completed: false,
          completedAt: null 
        },
      ],
      createdAt: '2026-01-05T08:00:00Z',
      completedAt: null,
    },
    {
      goalId: 'goal-003',
      employeeId: 'emp-103',
      employeeName: 'David Kim',
      employeeRole: 'Receiving Clerk',
      siteId: 'site-002',
      siteName: 'Deck 9 Buffet',
      title: 'Complete Food Safety Recertification',
      description: 'Renew food safety certification before expiration date.',
      category: 'safety',
      priority: 'high',
      status: 'completed',
      progress: 100,
      targetDate: '2026-01-31',
      assignedBy: 'user-001',
      assignedByName: 'Chef Martinez',
      createdAt: '2025-12-15T10:00:00Z',
      completedAt: '2026-01-28T16:30:00Z',
    },
    {
      goalId: 'goal-004',
      employeeId: 'emp-104',
      employeeName: 'Emily Brown',
      employeeRole: 'Sous Chef',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      title: 'Develop 5 New Seasonal Menu Items',
      description: 'Create and test 5 new dishes for the spring seasonal menu rotation.',
      category: 'development',
      priority: 'medium',
      status: 'in-progress',
      progress: 40,
      targetDate: '2026-04-15',
      assignedBy: 'user-001',
      assignedByName: 'Chef Martinez',
      milestones: [
        {
          id: 'm1',
          title: 'Research seasonal ingredients',
          completed: true,
          completedAt: '2026-01-20T10:00:00Z' 
        },
        {
          id: 'm2',
          title: 'Develop 2 appetizer concepts',
          completed: true,
          completedAt: '2026-01-28T14:00:00Z' 
        },
        {
          id: 'm3',
          title: 'Develop 2 entree concepts',
          completed: false,
          completedAt: null 
        },
        {
          id: 'm4',
          title: 'Develop 1 dessert concept',
          completed: false,
          completedAt: null 
        },
        {
          id: 'm5',
          title: 'Complete taste testing',
          completed: false,
          completedAt: null 
        },
      ],
      createdAt: '2026-01-10T09:00:00Z',
      completedAt: null,
    },
    {
      goalId: 'goal-005',
      employeeId: 'emp-101',
      employeeName: 'Maria Garcia',
      employeeRole: 'Line Cook',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      title: 'Improve Dish Consistency Score',
      description: 'Achieve 95% consistency score on quality audits for assigned station.',
      category: 'quality',
      priority: 'medium',
      status: 'at-risk',
      progress: 70,
      targetDate: '2026-02-15',
      assignedBy: 'user-001',
      assignedByName: 'Chef Martinez',
      createdAt: '2026-01-01T09:00:00Z',
      completedAt: null,
    },
  ],
  pagination: {
    nextToken: null,
    totalCount: 5,
  },
};

function buildGoalsQueryParams(filters?: GoalFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.employeeId) params.set('employeeId', filters.employeeId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.priority) params.set('priority', filters.priority);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.search) params.set('search', filters.search);
  return params;
}

async function fetchGoalsFromApi(filters?: GoalFilters): Promise<GoalListResponse> {
  const params = buildGoalsQueryParams(filters);
  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE_URL}/v1/performance/goals?${queryString}`
    : `${API_BASE_URL}/v1/performance/goals`;

  const response = await authFetch(url);

  if (!response.ok) {
    throw new GoalsApiError(response.status, `Failed to fetch goals: ${response.statusText}`);
  }

  const data: unknown = await response.json();
  return goalListResponseSchema.parse(data);
}

export async function getGoalsList(filters?: GoalFilters): Promise<GoalListResponse> {
  if (API_BASE_URL) {
    return fetchGoalsFromApi(filters);
  }

  await new Promise((resolve) => setTimeout(resolve, 500));
  
  const filtered = filterGoalsData(mockGoals.data, filters);

  return {
    data: filtered,
    pagination: {
      nextToken: null,
      totalCount: filtered.length,
    },
  };
}

function matchesGoalSearch(goal: Goal, search: string): boolean {
  const searchLower = search.toLowerCase();
  return (
    goal.employeeName.toLowerCase().includes(searchLower) ||
    goal.title.toLowerCase().includes(searchLower) ||
    goal.description.toLowerCase().includes(searchLower)
  );
}

function filterGoalsData(data: Goal[], filters?: GoalFilters): Goal[] {
  if (!filters) return data;
  
  return data.filter((g) => {
    if (filters.status && g.status !== filters.status) return false;
    if (filters.priority && g.priority !== filters.priority) return false;
    if (filters.category && g.category !== filters.category) return false;
    if (filters.search && !matchesGoalSearch(g, filters.search)) return false;
    return true;
  });
}

export class GoalsApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'GoalsApiError';
  }
}
